import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  Download,
  FileJson,
  FileSpreadsheet,
  Users,
  Tags,
  Filter,
  CheckCircle,
  FolderKanban,
  BarChart3
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { translateCategory } from "@/utils/translations";
import { TELEGRAM_CATEGORIES } from "@/constants/telegramCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExportCard = ({ icon: Icon, title, description, format, onExport, loading, jsonClicked, onJsonClick }) => (
  <div className="glass rounded-xl p-6 card-hover">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
        <Icon size={24} className="text-cyan-400" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-neutral-500 mt-1">{description}</p>
      </div>
    </div>
    <div className="flex gap-3 mt-6">
      <Button
        data-testid={`export-${format}-json`}
        className="flex-1 text-white font-semibold transition-all duration-300"
        style={{
          background: jsonClicked 
            ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
            : 'linear-gradient(135deg, #EF4444 0%, #DC2626 50%, #B91C1C 100%)',
          border: 'none',
          boxShadow: jsonClicked 
            ? '0 4px 15px rgba(16, 185, 129, 0.4)' 
            : '0 4px 15px rgba(239, 68, 68, 0.4)'
        }}
        onClick={() => {
          onJsonClick();
          onExport('json');
        }}
        disabled={loading}
      >
        <FileJson size={16} className="ml-2" />
        JSON
      </Button>
      <Button
        data-testid={`export-${format}-csv`}
        className="flex-1 btn-primary"
        onClick={() => onExport('csv')}
        disabled={loading}
      >
        <FileSpreadsheet size={16} className="ml-2" />
        CSV
      </Button>
    </div>
  </div>
);

export default function Export() {
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ members: 0, categories: 0 });
  const [membersJsonClicked, setMembersJsonClicked] = useState(false);
  const [categoriesJsonClicked, setCategoriesJsonClicked] = useState(false);
  const [mainCategoryStats, setMainCategoryStats] = useState({});
  const [subCategories, setSubCategories] = useState([]);
  const [groupStats, setGroupStats] = useState({});
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // Helper function to get localized category name
  const getCategoryDisplayName = (cat) => {
    const currentLang = i18n.language || 'ar';
    if (currentLang === 'en') {
      return cat.name_en || translateCategory(cat.name, t);
    }
    return cat.name_ar || cat.name;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, groupRes, statsRes, filterStatsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/groups`),
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/export/filter-stats`).catch(() => ({ data: {} }))
        ]);
        setCategories(catRes.data);
        setGroups(groupRes.data);
        setStats({
          members: statsRes.data.total_members,
          categories: statsRes.data.total_categories
        });
        
        // Set filter statistics
        if (filterStatsRes.data) {
          setMainCategoryStats(filterStatsRes.data.main_categories || {});
          setGroupStats(filterStatsRes.data.groups || {});
          
          // Build sub-categories list from filter stats (member interests)
          const subCats = filterStatsRes.data.sub_categories || {};
          const subCatList = Object.entries(subCats).map(([name, count], idx) => ({
            id: `subcat_${idx}`,
            name: name,
            name_ar: name,
            name_en: translateCategory(name, t) || name,
            members_count: count
          }));
          // Sort by count descending
          subCatList.sort((a, b) => b.members_count - a.members_count);
          setSubCategories(subCatList);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [t]);

  const handleExportMembers = async (format) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('format', format);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedMainCategory) params.append('main_category', selectedMainCategory);
      if (selectedGroup) params.append('group_id', selectedGroup);

      if (format === 'csv') {
        const response = await axios.get(`${API}/export/members?${params}`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'members.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('export.exportSuccess'));
      } else {
        const response = await axios.get(`${API}/export/members?${params}`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'members.json';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('export.exportSuccess'));
      }
    } catch (error) {
      toast.error(t('export.exportError'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCategories = async (format) => {
    try {
      setLoading(true);
      if (format === 'csv') {
        const response = await axios.get(`${API}/export/categories?format=csv`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'categories.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('export.exportSuccess'));
      } else {
        const response = await axios.get(`${API}/export/categories?format=json`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'categories.json';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(t('export.exportSuccess'));
      }
    } catch (error) {
      toast.error(t('export.exportError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="export-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-wide">{t('export.title')}</h1>
        <p className="text-neutral-500 mt-1">{t('export.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
            <Users size={24} className="text-green-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500">{t('export.totalMembers')}</p>
            <p className="text-2xl font-mono font-bold text-green-400">{stats.members}</p>
          </div>
        </div>
        <div className="glass rounded-xl p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Tags size={24} className="text-violet-400" />
          </div>
          <div>
            <p className="text-sm text-neutral-500">{t('export.totalCategories')}</p>
            <p className="text-2xl font-mono font-bold text-violet-400">{stats.categories}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={20} className="text-neutral-500" />
          <h3 className="font-semibold">{t('export.filterExport')}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Main Category Filter (from groups) */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2 flex items-center gap-2">
              <FolderKanban size={14} />
              {isRTL ? 'الفئات الرئيسية' : 'Main Categories'}
            </label>
            <Select value={selectedMainCategory || "all"} onValueChange={(val) => setSelectedMainCategory(val === "all" ? '' : val)}>
              <SelectTrigger data-testid="export-main-category-filter">
                <SelectValue placeholder={isRTL ? 'جميع الفئات الرئيسية' : 'All Main Categories'} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">
                  <div className="flex items-center justify-between w-full gap-4">
                    <span>{isRTL ? 'جميع الفئات الرئيسية' : 'All Main Categories'}</span>
                    <span className="text-xs text-neutral-500 bg-neutral-700/50 px-2 py-0.5 rounded-full">
                      {stats.members}
                    </span>
                  </div>
                </SelectItem>
                {TELEGRAM_CATEGORIES.map(cat => {
                  const count = mainCategoryStats[cat.id] || 0;
                  return (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center justify-between w-full gap-3">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span>{isRTL ? cat.name_ar : cat.name_en}</span>
                        </div>
                        {count > 0 && (
                          <span className="text-xs text-cyan-400 bg-cyan-500/20 px-2 py-0.5 rounded-full font-medium">
                            {count}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          {/* Sub-Category Filter (interests) */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2 flex items-center gap-2">
              <Tags size={14} />
              {isRTL ? 'الفئات الفرعية' : t('export.byCategory')}
            </label>
            <Select value={selectedCategory || "all"} onValueChange={(val) => setSelectedCategory(val === "all" ? '' : val)}>
              <SelectTrigger data-testid="export-category-filter">
                <SelectValue placeholder={isRTL ? 'جميع الفئات' : t('export.allCategories')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">
                  <div className="flex items-center justify-between w-full gap-4">
                    <span>{isRTL ? 'جميع الفئات' : t('export.allCategories')}</span>
                    <span className="text-xs text-neutral-500 bg-neutral-700/50 px-2 py-0.5 rounded-full">
                      {stats.members}
                    </span>
                  </div>
                </SelectItem>
                {subCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name}>
                    <div className="flex items-center justify-between w-full gap-3">
                      <span>{getCategoryDisplayName(cat)}</span>
                      <span className="text-xs text-violet-400 bg-violet-500/20 px-2 py-0.5 rounded-full font-medium">
                        {cat.members_count}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Group Filter */}
          <div>
            <label className="text-sm text-neutral-400 block mb-2 flex items-center gap-2">
              <Users size={14} />
              {isRTL ? 'حسب المجموعة' : t('export.byGroup')}
            </label>
            <Select value={selectedGroup || "all"} onValueChange={(val) => setSelectedGroup(val === "all" ? '' : val)}>
              <SelectTrigger data-testid="export-group-filter">
                <SelectValue placeholder={isRTL ? 'جميع المجموعات' : t('export.allGroups')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">
                  <div className="flex items-center justify-between w-full gap-4">
                    <span>{isRTL ? 'جميع المجموعات' : t('export.allGroups')}</span>
                    <span className="text-xs text-neutral-500 bg-neutral-700/50 px-2 py-0.5 rounded-full">
                      {stats.members}
                    </span>
                  </div>
                </SelectItem>
                {groups.map(g => {
                  const count = groupStats[g.id] || g.members_count || 0;
                  return (
                    <SelectItem key={g.id} value={g.id}>
                      <div className="flex items-center justify-between w-full gap-3">
                        <span>{g.group_name}</span>
                        {count > 0 && (
                          <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full font-medium">
                            {count}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ExportCard
          icon={Users}
          title={t('export.exportMembers')}
          description={t('export.exportMembersDesc')}
          format="members"
          onExport={handleExportMembers}
          loading={loading}
          jsonClicked={membersJsonClicked}
          onJsonClick={() => {
            setMembersJsonClicked(true);
            setTimeout(() => setMembersJsonClicked(false), 2000);
          }}
        />
        <ExportCard
          icon={Tags}
          title={t('export.exportCategories')}
          description={t('export.exportCategoriesDesc')}
          format="categories"
          onExport={handleExportCategories}
          loading={loading}
          jsonClicked={categoriesJsonClicked}
          onJsonClick={() => {
            setCategoriesJsonClicked(true);
            setTimeout(() => setCategoriesJsonClicked(false), 2000);
          }}
        />
      </div>

      {/* Info */}
      <div className="glass rounded-xl p-4 flex items-start gap-3">
        <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-green-400">{t('export.note')}</h4>
          <p className="text-sm text-neutral-500 mt-1">
            {t('export.noteDescription')}
          </p>
        </div>
      </div>
    </div>
  );
}
